// frontend/src/pages/ConsultancyDetails.js
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL } from "../config";
import { getActiveOffers } from "../utils/offerUtils";

export default function ConsultancyDetails() {
  const { id } = useParams();
  const [consultancy, setConsultancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingLead, setCreatingLead] = useState(false);
  const [addingWishlist, setAddingWishlist] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  useEffect(() => {
    const fetchConsultancy = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/consultancies/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        const sanitized = data
          ? { ...data, offers: getActiveOffers(data.offers) }
          : null;
        setConsultancy(sanitized);
      } catch (err) {
        console.error("Error loading consultancy", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsultancy();
  }, [id, token]);

  const handleCreateLead = async () => {
    if (!token || !user) {
      alert("Please login as a student to contact this consultancy.");
      return;
    }

    setCreatingLead(true);
    try {
      const res = await fetch(`${BACKEND_URL}/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          consultancyId: consultancy._id,
          studentName: user.name || "",
          studentEmail: user.email || "",
          studentPhone: user.phone || "",
          message: "Student is interested in this consultancy.",
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body.error || "Error creating lead");
        return;
      }

      alert("Your enquiry has been sent!");
    } catch (err) {
      console.error("Lead error", err);
      alert("Error creating lead");
    } finally {
      setCreatingLead(false);
    }
  };

  const handleAddWishlist = async () => {
    if (!token || !user) {
      alert("Please login as a student to use wishlist.");
      return;
    }

    setAddingWishlist(true);
    try {
      const res = await fetch(`${BACKEND_URL}/wishlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          consultancyId: consultancy._id,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body.error || "Error adding to wishlist");
        return;
      }

      alert("Added to wishlist!");
    } catch (err) {
      console.error("Wishlist error", err);
      alert("Error adding to wishlist");
    } finally {
      setAddingWishlist(false);
    }
  };

  const handleClaim = async () => {
    if (!token || !user) {
      alert("Please login to request ownership review.");
      return;
    }
    const businessEmail = window.prompt("Business email for claim verification");
    const businessPhone = window.prompt("Business phone for claim verification");
    if (!businessEmail && !businessPhone) return;

    setClaiming(true);
    try {
      const res = await fetch(`${BACKEND_URL}/consultancies/${consultancy._id}/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ businessEmail, businessPhone }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body.error || "Could not submit claim");
        return;
      }
      alert("Claim request submitted for admin review.");
    } catch (err) {
      console.error("Claim request error", err);
      alert("Could not submit claim");
    } finally {
      setClaiming(false);
    }
  };

  const renderRating = () => {
    const rating = consultancy?.rating ?? 0; // expect 0–5
    const totalStars = 5;

    return (
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {[...Array(totalStars)].map((_, idx) => {
            const filled = idx < rating;
            return (
              <span
                key={idx}
                className={
                  filled
                    ? "text-yellow-400 text-xl drop-shadow"
                    : "text-white/30 text-xl"
                }
              >
                ★
              </span>
            );
          })}
        </div>
        <div className="px-3 py-1 rounded-full bg-white/10 text-sm font-semibold">
          {rating ? `${rating.toFixed(1)} / 5` : "Awaiting reviews"}
        </div>
      </div>
    );
  };

  const renderTopOffer = () => {
    const offers = getActiveOffers(consultancy?.offers);
    if (offers.length === 0) {
      return (
        <div className="rounded-2xl border border-white/5 bg-white/5 p-6 text-white/70 text-center">
          No active offers available
        </div>
      );
    }

    const top = offers[0];

    return (
      <div className="rounded-3xl bg-gradient-to-r from-amber-500 to-orange-600 text-white p-6 shadow-xl">
        <div className="text-sm uppercase tracking-[4px] font-semibold text-white/80">
          Signature Offer
        </div>
        <h3 className="text-2xl font-bold mt-2">
          {top.title || "Limited Time Excellence"}
        </h3>
        {top.description && (
          <p className="mt-3 text-white/90 text-sm md:text-base">
            {top.description}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          {top.discount && (
            <span className="px-4 py-2 rounded-full bg-white/20 font-semibold">
              {top.discount}
            </span>
          )}
          {top.validTill && (
            <span className="px-4 py-2 rounded-full bg-black/20 font-semibold">
              Ends{" "}
              {new Date(top.validTill).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderOtherOffers = () => {
    const offers = getActiveOffers(consultancy?.offers);
    if (offers.length <= 1) return null;

    return (
      <div className="grid gap-4 md:grid-cols-2">
        {offers.slice(1).map((o, idx) => (
          <div
            key={idx}
            className="rounded-2xl border border-white/10 bg-white/5 p-4 text-white/90"
          >
            <div className="text-lg font-semibold mb-1">
              {o.title || "Offer"}
            </div>
            {o.description && (
              <p className="text-sm text-white/70">{o.description}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {o.discount && (
                <span className="px-3 py-1 rounded-full bg-white/10 font-semibold">
                  {o.discount}
                </span>
              )}
              {o.validTill && (
                <span className="px-3 py-1 rounded-full bg-white/10">
                  Ends{" "}
                  {new Date(o.validTill).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50">
        <div className="text-blue-700 font-semibold">
          Loading consultancy...
        </div>
      </div>
    );
  }

  if (!consultancy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50">
        <div className="text-red-600 font-semibold">
          Consultancy not found.
        </div>
      </div>
    );
  }

  const countryValues = consultancy.countriesServed || consultancy.countries;
  const countriesArr = Array.isArray(countryValues)
    ? countryValues
    : countryValues
    ? [countryValues]
    : [];
  const serviceValues = consultancy.services || consultancy.courses;
  const coursesArr = Array.isArray(serviceValues)
    ? serviceValues
    : serviceValues
    ? [serviceValues]
    : [];
  const countriesText =
    countriesArr.length > 0 ? countriesArr.join(", ") : "Countries not listed";

  const description =
    consultancy.description || "No description provided.";

  const locationText =
    [consultancy.city, consultancy.state].filter(Boolean).join(", ") ||
    consultancy.location ||
    "Location not specified.";

  const phone =
    consultancy.phone || consultancy.contactNumber || "Not provided";

  const address = consultancy.address || consultancy.location || "Address not provided";

  const website = consultancy.website || consultancy.websiteUrl || "";
  const locationUrl = consultancy.locationUrl || consultancy.mapsUrl || "";

  const trustLabel = consultancy.verificationStatus === "PUBLISHED" || consultancy.verificationStatus === "VERIFIED"
    ? "Verified"
    : consultancy.claimed
    ? "Claimed"
    : "Unverified";

  const heroStats = [
    {
      label: "Destinations",
      value: countriesArr.length ? `${countriesArr.length}+` : "Global",
      hint: "Countries empowered",
    },
    {
      label: "Programs",
      value: coursesArr.length ? `${coursesArr.length}+` : "Multi-discipline",
      hint: "Study tracks curated",
    },
    {
      label: "Trust Status",
      value: trustLabel,
      hint: "AbroadJacks review state",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_55%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-10 lg:py-24">
          <div className="grid gap-6 md:grid-cols-[160px,1fr] md:items-center lg:grid-cols-[180px,1fr]">
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-3xl font-bold uppercase tracking-wider backdrop-blur sm:h-36 sm:w-36 md:h-40 md:w-40 lg:h-44 lg:w-44">
              {consultancy.name?.[0] || "E"}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[4px] text-white/60 sm:text-sm sm:tracking-[6px]">
                Premium Consultancy Partner
              </p>
              <h1 className="mt-3 text-[clamp(2rem,8vw,3.5rem)] font-semibold leading-tight">
                {consultancy.name}
              </h1>
              <p className="mt-4 text-white/80 max-w-2xl">
                {description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                {renderRating()}
                <span className="px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold border border-white/20">
                  {trustLabel}
                </span>
                {consultancy.subscribed && (
                  <span className="px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-200 text-sm font-semibold border border-emerald-400/40">
                    Priority Partner
                  </span>
                )}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  onClick={handleCreateLead}
                  disabled={creatingLead}
                  className="min-h-[44px] rounded-full bg-white px-6 py-3 font-semibold text-blue-900 transition hover:bg-slate-100 disabled:opacity-60"
                >
                  {creatingLead ? "Connecting..." : "Book a Priority Call"}
                </button>
                <button
                  onClick={handleAddWishlist}
                  disabled={addingWishlist}
                  className="min-h-[44px] rounded-full border border-white/40 px-6 py-3 font-semibold text-white transition hover:bg-white/10 disabled:opacity-60"
                >
                  {addingWishlist ? "Adding..." : "Add to Wishlist"}
                </button>
                {!consultancy.claimed && (
                  <button
                    onClick={handleClaim}
                    disabled={claiming}
                    className="min-h-[44px] rounded-full border border-emerald-300/50 px-6 py-3 font-semibold text-emerald-100 transition hover:bg-emerald-400/10 disabled:opacity-60"
                  >
                    {claiming ? "Submitting..." : "Claim this Consultancy"}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5"
              >
                <div className="text-sm uppercase tracking-[4px] text-white/60">
                  {stat.label}
                </div>
                <div className="text-3xl font-bold mt-2">{stat.value}</div>
                <p className="text-sm text-white/70 mt-2">{stat.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative -mt-12 pb-20">
        <div className="mx-auto max-w-6xl space-y-8 px-4 sm:px-6 lg:px-10 lg:space-y-12">
          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur sm:rounded-3xl sm:p-8">
              <div className="flex flex-wrap gap-3">
                {(countriesArr.length ? countriesArr : ["Global Presence"]).map(
                  (c) => (
                    <span
                      key={c}
                      className="px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-200 text-xs tracking-wide border border-blue-500/30"
                    >
                      {c}
                    </span>
                  )
                )}
              </div>
              <div className="mt-6 grid gap-6 text-sm text-white/80 md:grid-cols-2">
                <div>
                  <p className="text-white/50 uppercase text-xs tracking-[3px] mb-2">
                    Headquarters
                  </p>
                  <p className="text-lg font-semibold text-white">{locationText}</p>
                  {locationUrl && (
                    <a
                      href={locationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-300 hover:text-blue-200 mt-1 inline-block"
                    >
                      Open in Maps ↗
                    </a>
                  )}
                  <p className="mt-4 text-white/50 uppercase text-xs tracking-[3px]">
                    Address
                  </p>
                  <p className="text-white">{address}</p>
                </div>
                <div>
                  <p className="text-white/50 uppercase text-xs tracking-[3px] mb-2">
                    Connect
                  </p>
                  <p className="text-white">
                    <span className="text-white/60">Email:</span>{" "}
                    {consultancy.email || "Not provided"}
                  </p>
                  <p className="text-white mt-2">
                    <span className="text-white/60">Phone:</span> {phone}
                  </p>
                  {website && (
                    <p className="text-white mt-2">
                      <span className="text-white/60">Website:</span>{" "}
                      <a
                        href={
                          website.startsWith("http")
                            ? website
                            : `https://${website}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-300 hover:text-blue-200"
                      >
                        {website}
                      </a>
                    </p>
                  )}
                  {(consultancy.instagram || consultancy.facebook || consultancy.linkedin || consultancy.youtube) && (
                    <div className="mt-4">
                      <p className="text-white/50 uppercase text-xs tracking-[3px] mb-2">
                        Social
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {["instagram", "facebook", "linkedin", "youtube"].map((key) => (
                          consultancy[key] ? (
                            <a
                              key={key}
                              href={consultancy[key]}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-300 hover:text-blue-200 capitalize"
                            >
                              {key}
                            </a>
                          ) : null
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6 rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-400/10 to-blue-500/10 p-5 backdrop-blur sm:rounded-3xl sm:p-8">
              <div>
                <p className="text-white/50 uppercase text-xs tracking-[3px]">
                  Signature Expertise
                </p>
                <p className="text-xl font-semibold mt-2 text-white">
                  {coursesArr.length
                    ? coursesArr.slice(0, 3).join(" · ")
                    : "Multiple disciplines"}
                </p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-sm text-white/80">
                {consultancy.verificationStatus === "VERIFIED" || consultancy.verificationStatus === "PUBLISHED"
                  ? "This listing has passed AbroadJacks review checks."
                  : "This listing is not marked verified by AbroadJacks yet."}
              </div>
            </div>
          </div>

          {Array.isArray(consultancy.branches) && consultancy.branches.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:rounded-3xl sm:p-8">
              <p className="text-white/50 uppercase text-xs tracking-[4px]">Branches</p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {consultancy.branches.map((branch) => (
                  <div key={branch._id || branch.address} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <p className="font-semibold">{branch.branchName || branch.city || "Branch"}</p>
                    <p className="mt-1 text-sm text-white/70">{branch.address || "Address not listed"}</p>
                    <p className="mt-1 text-sm text-white/60">{[branch.city, branch.state].filter(Boolean).join(", ")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-6 rounded-2xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur sm:rounded-3xl sm:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-white/50 uppercase text-xs tracking-[4px]">
                  Offers & Experiences
                </p>
                <h2 className="mt-2 text-xl font-semibold sm:text-2xl">
                  Tailored perks for visionary students
                </h2>
              </div>
              <div className="text-sm text-white/60">
                {locationText} • {countriesText}
              </div>
            </div>
            {renderTopOffer()}
            {renderOtherOffers()}
          </div>

          <div className="flex flex-col gap-6 rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 p-5 shadow-2xl sm:rounded-3xl sm:p-8 md:flex-row md:items-center md:justify-between lg:p-10">
            <div>
              <p className="text-sm uppercase tracking-[4px] text-white/70">
                Elevate Your Journey
              </p>
              <h3 className="mt-2 text-2xl font-semibold sm:text-3xl">
                Speak with a lead strategist within 24 hours
              </h3>
              <p className="text-white/80 mt-3 max-w-2xl text-sm">
                Priority responses, curated shortlists, scholarship prep, and
                visa hand-holding built for ambitious learners.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={handleCreateLead}
                disabled={creatingLead}
                className="min-h-[44px] flex-1 rounded-full bg-white px-6 py-3 font-semibold text-blue-900 transition hover:bg-slate-100 disabled:opacity-60"
              >
                {creatingLead ? "Connecting..." : "Request Elite Guidance"}
              </button>
              <button
                onClick={handleAddWishlist}
                disabled={addingWishlist}
                className="min-h-[44px] flex-1 rounded-full border border-white/60 px-6 py-3 font-semibold text-white transition hover:bg-white/10 disabled:opacity-60"
              >
                {addingWishlist ? "Adding..." : "Save for Later"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
